/**
 * Rubrica Contatti — lista, ricerca, preferiti, import dai messaggi,
 * CRUD e arricchimento AI (estrazione dati da firme/email recenti).
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search, Star, Plus, Download, Sparkles, Trash2, Mail, Loader2, X, Building2, Phone, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useInbox } from '@/stores/inbox.store';
import { useComposer } from '@/stores/composer.store';
import { contactsApi, accountsApi, aiApi, type Contact, type ContactInput } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';

const EMPTY: ContactInput = { email: '', name: '', company: '', role: '', phone: '', notes: '', is_favorite: false };

export function ContactsPage() {
  const qc = useQueryClient();
  const openCompose = useComposer((s) => s.openCompose);
  const selectedAccountId = useInbox((s) => s.selectedAccountId);

  const [search, setSearch] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<ContactInput>(EMPTY);
  const [summaryContact, setSummaryContact] = useState<Contact | null>(null);

  const summaryQuery = useQuery({
    queryKey: ['contact-summary', summaryContact?.email],
    queryFn: () => aiApi.contactSummary(summaryContact!.email),
    enabled: !!summaryContact,
    staleTime: 5 * 60_000,
  });

  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => accountsApi.list() });
  const accountId =
    selectedAccountId ??
    accountsQuery.data?.accounts.find((a) => a.is_default)?.id ??
    accountsQuery.data?.accounts[0]?.id;

  const contactsQuery = useQuery({
    queryKey: ['contacts', search, favoritesOnly],
    queryFn: () => contactsApi.list({ search: search || undefined, favoritesOnly }),
  });
  const contacts = contactsQuery.data?.contacts ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['contacts'] });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editing) return contactsApi.update(editing.id, form);
      if (!accountId) throw new Error('Nessun account disponibile');
      return contactsApi.create(accountId, form);
    },
    onSuccess: () => { invalidate(); closeDialog(); toast.success(editing ? 'Contatto aggiornato' : 'Contatto creato'); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Errore salvataggio'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => contactsApi.remove(id),
    onSuccess: () => { invalidate(); closeDialog(); toast.success('Contatto eliminato'); },
  });

  const importMutation = useMutation({
    mutationFn: () => { if (!accountId) throw new Error('Nessun account'); return contactsApi.import(accountId); },
    onSuccess: (r) => { invalidate(); toast.success(`${r.imported} contatti importati dalle email`); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Import fallito'),
  });

  const enrichMutation = useMutation({
    mutationFn: (id: string) => contactsApi.enrich(id),
    onSuccess: (r) => { invalidate(); setForm((f) => ({ ...f, name: r.contact.name, company: r.contact.company, role: r.contact.role, phone: r.contact.phone })); toast.success('Contatto arricchito con AI'); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Arricchimento fallito'),
  });

  const favMutation = useMutation({
    mutationFn: (c: Contact) => contactsApi.update(c.id, { is_favorite: !c.is_favorite }),
    onSuccess: invalidate,
  });

  function openCreate() { setEditing(null); setForm(EMPTY); setCreating(true); }
  function openEdit(c: Contact) {
    setEditing(c);
    setForm({ email: c.email, name: c.name, company: c.company, role: c.role, phone: c.phone, notes: c.notes, is_favorite: c.is_favorite });
    setCreating(true);
  }
  function closeDialog() { setCreating(false); setEditing(null); setForm(EMPTY); }

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />

      <main className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="border-b px-6 py-4 flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-bold mr-2">Rubrica</h1>
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Cerca per nome, email, azienda…"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button
            variant={favoritesOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFavoritesOnly((v) => !v)}
          >
            <Star className={`h-4 w-4 mr-1.5 ${favoritesOnly ? 'fill-current' : ''}`} /> Preferiti
          </Button>
          <Button variant="outline" size="sm" onClick={() => importMutation.mutate()} disabled={importMutation.isPending || !accountId}>
            {importMutation.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
            Importa dalle email
          </Button>
          <Button size="sm" onClick={openCreate} disabled={!accountId}>
            <Plus className="h-4 w-4 mr-1.5" /> Nuovo
          </Button>
        </div>

        {/* Lista */}
        <div className="flex-1 overflow-y-auto p-6">
          {contactsQuery.isLoading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              <Mail className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <div className="text-sm">Nessun contatto.</div>
              <div className="text-xs mt-1">Premi “Importa dalle email” per popolare la rubrica dai mittenti.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 max-w-6xl">
              {contacts.map((c) => (
                <div key={c.id} className="group rounded-lg border p-4 hover:border-primary/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold flex-shrink-0">
                      {(c.name || c.email).charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium truncate">{c.name || c.email}</span>
                        <button onClick={() => favMutation.mutate(c)} className="flex-shrink-0" title="Preferito">
                          <Star className={`h-3.5 w-3.5 ${c.is_favorite ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/40 hover:text-amber-500'}`} />
                        </button>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                      {(c.company || c.role) && (
                        <div className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-0.5">
                          <Building2 className="h-3 w-3" /> {[c.role, c.company].filter(Boolean).join(' · ')}
                        </div>
                      )}
                      {c.phone && (
                        <div className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-0.5">
                          <Phone className="h-3 w-3" /> {c.phone}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => openCompose({ to: [{ name: c.name ?? undefined, address: c.email }] })}>
                      <Mail className="h-3.5 w-3.5 mr-1" /> Scrivi
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => openEdit(c)}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Modifica
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-primary" onClick={() => setSummaryContact(c)}>
                      <Sparkles className="h-3.5 w-3.5 mr-1" /> Scheda AI
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Dialog crea/modifica */}
      <Dialog open={creating} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifica contatto' : 'Nuovo contatto'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@esempio.it" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nome</Label>
                <Input value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>Telefono</Label>
                <Input value={form.phone ?? ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Azienda</Label>
                <Input value={form.company ?? ''} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              </div>
              <div>
                <Label>Ruolo</Label>
                <Input value={form.role ?? ''} onChange={(e) => setForm({ ...form, role: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_favorite ?? false} onCheckedChange={(v) => setForm({ ...form, is_favorite: v })} />
              <Label className="cursor-pointer">Preferito</Label>
              {editing && (
                <Button variant="ghost" size="sm" className="ml-auto" onClick={() => enrichMutation.mutate(editing.id)} disabled={enrichMutation.isPending}>
                  {enrichMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                  Arricchisci con AI
                </Button>
              )}
            </div>
          </div>
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            {editing ? (
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => deleteMutation.mutate(editing.id)}>
                <Trash2 className="h-4 w-4 mr-1" /> Elimina
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={closeDialog}><X className="h-4 w-4 mr-1" /> Annulla</Button>
              <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !form.email}>
                {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Salva
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scheda cliente AI */}
      <Dialog open={!!summaryContact} onOpenChange={(o) => { if (!o) setSummaryContact(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" /> Scheda di {summaryContact?.name || summaryContact?.email}
            </DialogTitle>
          </DialogHeader>
          {summaryQuery.isLoading ? (
            <div className="flex items-center gap-2 py-10 justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> L'AI sta analizzando la relazione…
            </div>
          ) : summaryQuery.isError ? (
            <div className="py-6 text-sm text-destructive">Impossibile generare la scheda.</div>
          ) : (
            <>
              <div className="text-sm whitespace-pre-wrap leading-relaxed max-h-[60vh] overflow-y-auto">
                {summaryQuery.data?.summary}
              </div>
              {summaryQuery.data && summaryQuery.data.count > 0 && (
                <p className="text-xs text-muted-foreground mt-1">Basato su {summaryQuery.data.count} email recenti.</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
